import React from "react";
import styled from "styled-components";
import Input from "sushiweb/InputTypes/Input";
import Dropdown from "sushiweb/Dropdown";
import MenuItem from "sushiweb/MenuItem/MenuItem";
import Checkbox from "sushiweb/InputTypes/Checkbox";
import { DeleteBar, FlexContainer } from "../../../constants/assets/containers";
import PlusCircle from "sushiweb/Icons/all/PlusCircle";
import Delete from "sushiweb/Icons/all/Delete";
import variables from "../../../constants/assets/variables";
import RenderIf from "../../Common/RenderIf/RenderIf";
import { InputBase, Radio, TextField } from "@mui/material";
import { audioUrlRegex } from "../../../utils/audio";
import { ErrorLabel } from "../../CommunicationHub/ElementForm";

const ASSESSMENT_TYPE = {
  QUIZ: "quiz",
  SURVEY: "survey",
};

const MODE = {
  VIEW: "view",
  CREATE: "create",
};

const QuizComponent = ({
  items,
  handleSectionChange,
  secIndex,
  handleQuizOptionAddition,
  handleQuizQuestionAddition,
  handleQuizQuestionDelete,
  handleOptionDelete,
  disableQuestionEditing,
  type = ASSESSMENT_TYPE.QUIZ,
  singleChoiceValue = "single",
  multipleChoiceValue = "multi",
  mode = MODE.CREATE,
  showAudioLink = false,
}) => {
  return (
    <QuizContainer>
      {items.map((item, index) => {
        return (
          <QuestionContainer
            disable={disableQuestionEditing(secIndex, index)}
            key={index}
          >
            <DisabledContainer
              disable={disableQuestionEditing(secIndex, index)}
            >
              <FlexContainer align="flex-start">
                <QuestionHolder align="flex-start">
                  <Input
                    onChange={(e) =>
                      handleSectionChange(secIndex, "question", e, type, index)
                    }
                    value={item.question}
                    innerWidth="100%"
                    id="sushi-input"
                    label={"Question " + (index + 1)}
                  />
                  {item.choices.map((choice, idx) => {
                    return (
                      <FlexContainer
                        key={index + idx}
                        className="mb-20"
                        justify="flex-start"
                      >
                        {type === ASSESSMENT_TYPE.QUIZ &&
                          (item.type === singleChoiceValue ? (
                            <Radio
                              color="error"
                              checked={(item.answers || []).includes(idx + 1)}
                              onChange={(e) =>
                                handleSectionChange(
                                  secIndex,
                                  "answers",
                                  e,
                                  type,
                                  index,
                                  idx
                                )
                              }
                            />
                          ) : (
                            <Checkbox
                              color={variables.red500}
                              checked={(item.answers || []).includes(idx + 1)}
                              onChange={(e) =>
                                handleSectionChange(
                                  secIndex,
                                  "answers",
                                  e,
                                  type,
                                  index,
                                  idx
                                )
                              }
                              className="sushi-checkbox mr-10"
                            />
                          ))}
                        <Input
                          onChange={(e) =>
                            handleSectionChange(
                              secIndex,
                              "choices",
                              e,
                              type,
                              index,
                              idx
                            )
                          }
                          value={choice}
                          innerWidth="100%"
                          id="sushi-input"
                          className="choice-input mr-10"
                          label={"Option " + (idx + 1)}
                        />
                        {idx !== 0 && (
                          <Delete
                            onClick={() =>
                              handleOptionDelete(secIndex, index, idx)
                            }
                            className="delete-option"
                            size={14}
                            color={variables.red500}
                          />
                        )}
                      </FlexContainer>
                    );
                  })}
                  <AddOptionContainer
                    onClick={() => handleQuizOptionAddition(secIndex, index)}
                  >
                    Add Option
                  </AddOptionContainer>
                </QuestionHolder>
                <Dropdown
                  value={
                    item.type === singleChoiceValue
                      ? "Single Choice"
                      : "Multiple Choice"
                  }
                  borderColor={variables.primaryColor}
                  className="sushi-dropdown"
                  onSelect={(e) =>
                    handleSectionChange(secIndex, "type", e, type, index)
                  }
                >
                  <MenuItem value={singleChoiceValue} label={"Single Choice"} />
                  <MenuItem
                    value={multipleChoiceValue}
                    label={"Multiple Choice"}
                  />
                </Dropdown>
              </FlexContainer>
            </DisabledContainer>

            <RenderIf condition={showAudioLink}>
              <FlexContainer style={{ gap: 8, marginBottom: 8 }}>
                <RenderIf condition={mode === MODE.CREATE}>
                  <InputBase
                    onChange={(e) =>
                      handleSectionChange(
                        secIndex,
                        "link",
                        e.target.value,
                        type,
                        index
                      )
                    }
                    sx={{ flex: 1, maxWidth: "50%" }}
                    placeholder="Audio link"
                    variant="outlined"
                    value={item.link}
                    innerWidth="100%"
                    label={"Audio link " + (index + 1)}
                  />
                </RenderIf>
                <RenderIf
                  condition={!!item.link && !audioUrlRegex.test(item.link)}
                >
                  <ErrorLabel label={"Please enter a valid link"} />
                </RenderIf>
                <RenderIf condition={audioUrlRegex.test(item.link)}>
                  <audio src={item.link} controls />
                </RenderIf>
              </FlexContainer>
            </RenderIf>
            <InfoBar>
              {type === ASSESSMENT_TYPE.QUIZ && (
                <p>Make sure you select the right answer above</p>
              )}
              <RenderIf condition={mode === MODE.CREATE}>
                <DeleteBar
                  onClick={() => handleQuizQuestionDelete(secIndex, index)}
                  style={{ marginLeft: "auto" }}
                >
                  <Delete
                    className="mr-10"
                    size={20}
                    color={variables.red500}
                  />
                  <span>Delete</span>
                </DeleteBar>
              </RenderIf>
            </InfoBar>
          </QuestionContainer>
        );
      })}
      <RenderIf condition={mode === MODE.CREATE}>
        <AddQuestionContainer
          onClick={() => handleQuizQuestionAddition(secIndex)}
          justify={"flex-start"}
        >
          <PlusCircle color={variables.red500} size={20} />
          <span>Add Question</span>
        </AddQuestionContainer>
      </RenderIf>
    </QuizContainer>
  );
};

const InfoBar = styled(FlexContainer)`
  border-top: 1px solid ${variables.grey300};
  padding-top: 20px;
  p {
    color: ${variables.grey600};
  }
`;

const QuizContainer = styled.section``;

const AddOptionContainer = styled.p`
  color: ${variables.red500};
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 1.5rem;
`;

const AddQuestionContainer = styled(FlexContainer)`
  span {
    color: ${variables.red500};
    font-weight: 500;
    margin-left: 5px;
  }
  cursor: pointer;
  margin: 0.65rem 0 1.25rem 0;
`;

const QuestionContainer = styled.div`
  background: ${variables.grey50};
  opacity: ${({ disable = false }) => (disable ? 0.7 : 1)};
  border: 1px solid ${variables.grey300};
  box-sizing: border-box;
  border-radius: 0.75rem;
  padding: 1.9rem;
  margin-bottom: 1.9rem;
`;
const DisabledContainer = styled.div`
  pointer-events: ${({ disable = false }) => (disable ? "none" : "all")};
`;

const QuestionHolder = styled.div`
  flex: 0 1 60%;
  .choice-input {
    margin-bottom: unset !important;
  }
  #sushi-input {
    width: 100%;
  }
  .delete-option {
    cursor: pointer;
  }
`;

export default QuizComponent;
